import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Badge, ErrorBox, Loading, Tabs, useAsync } from '../../shared/ui';
import { api, tenantPath } from '../api';
import { Icon } from '../components/Icons';
import { PageHead } from '../components/PageHead';
import { SubscriptionBadge } from '../components/SubscriptionBadge';
import type { TenantDetail } from '../types';
import { ActivityTab } from './tenant/ActivityTab';
import { BrandingTab } from './tenant/BrandingTab';
import { ConnectionTab } from './tenant/ConnectionTab';
import { OutboxTab } from './tenant/OutboxTab';
import { SettingsTab } from './tenant/SettingsTab';
import { SummaryTab } from './tenant/SummaryTab';
import { UsersTab } from './tenant/UsersTab';

export type TenantTab = 'resumen' | 'conexion' | 'usuarios' | 'configuracion' | 'marca' | 'avisos' | 'actividad';

const TABS: Array<{ id: TenantTab; label: string }> = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'conexion', label: 'Conexión SAP' },
  { id: 'usuarios', label: 'Usuarios' },
  { id: 'configuracion', label: 'Configuración' },
  { id: 'marca', label: 'Marca y contrato' },
  { id: 'avisos', label: 'Avisos' },
  { id: 'actividad', label: 'Actividad' },
];

/** Lo que recibe cada pestaña: el cliente ya cargado y cómo recargarlo tras un cambio. */
export interface TenantTabProps {
  slug: string;
  detail: TenantDetail;
  reload: () => Promise<void>;
  goTo: (tab: TenantTab) => void;
}

export function TenantDetailPage(): JSX.Element {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const detail = useAsync(() => api.get<TenantDetail>(tenantPath(slug)), [slug]);

  const requested = params.get('tab') as TenantTab | null;
  const tab: TenantTab = TABS.some((t) => t.id === requested) ? (requested as TenantTab) : 'resumen';
  const goTo = (next: TenantTab): void => {
    setParams(next === 'resumen' ? {} : { tab: next }, { replace: true });
    window.scrollTo({ top: 0 });
  };

  const crumbs = (
    <>
      <Link to="/clientes">Clientes</Link> / <span className="mono">{slug}</span>
    </>
  );

  if (!detail.data) {
    return (
      <div className="stack lg">
        <PageHead crumbs={crumbs} title={detail.error ? 'Cliente no disponible' : 'Cargando cliente…'} />
        {detail.error ? <ErrorBox error={detail.error} onRetry={() => void detail.reload()} /> : <Loading />}
      </div>
    );
  }

  const { tenant } = detail.data;
  const props: TenantTabProps = { slug, detail: detail.data, reload: detail.reload, goTo };

  return (
    <div className="stack">
      <PageHead
        crumbs={crumbs}
        title={tenant.name}
        description={
          <span className="row tenant-meta">
            <span className="mono">{tenant.slug}</span>
            <a href={tenant.portalUrl} target="_blank" rel="noreferrer">
              {tenant.portalUrl.replace(/^https?:\/\//, '')} <Icon name="external" size={13} />
            </a>
            <SubscriptionBadge status={tenant.subscription.status} />
            {!tenant.active ? <Badge tone="bad">Desactivado</Badge> : null}
            <span className="subtle">Túnel {tenant.hubClient}</span>
          </span>
        }
      />
      {detail.error ? <ErrorBox error={detail.error} onRetry={() => void detail.reload()} /> : null}
      <Tabs tabs={TABS} value={tab} onChange={goTo} />
      {tab === 'resumen' ? <SummaryTab {...props} /> : null}
      {tab === 'conexion' ? <ConnectionTab {...props} /> : null}
      {tab === 'usuarios' ? <UsersTab {...props} /> : null}
      {tab === 'configuracion' ? <SettingsTab {...props} /> : null}
      {tab === 'marca' ? <BrandingTab {...props} /> : null}
      {tab === 'avisos' ? <OutboxTab {...props} /> : null}
      {tab === 'actividad' ? <ActivityTab {...props} /> : null}
    </div>
  );
}
